import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, filter } from 'rxjs';

import { ApiUrlService } from './api-url.service';
import { StoragePortalService } from './storage-portal.service';
import {
  ChatConnectionErrorPayload,
  ChatPingPayload,
  ChatSocketEvent,
  ChatSocketEventType
} from '../models/chat';

export type ChatSocketConnectionState =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closing'
  | 'closed'
  | 'error';

export interface ChatSocketConnectOptions {
  reconnect?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ChatSocketService implements OnDestroy {
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private intentionalClose = false;
  private reconnectEnabled = true;

  private readonly reconnectBaseDelayMs = 1000;
  private readonly reconnectMaxDelayMs = 15000;
  private readonly heartbeatIntervalMs = 30000;
  private readonly eventsSubject = new Subject<ChatSocketEvent>();
  private readonly connectionStateSubject = new BehaviorSubject<ChatSocketConnectionState>('idle');

  readonly events$ = this.eventsSubject.asObservable();
  readonly connectionState$ = this.connectionStateSubject.asObservable();

  constructor(
    private apiUrl: ApiUrlService,
    private storage: StoragePortalService,
    private zone: NgZone
  ) {}

  ngOnDestroy(): void {
    this.disconnect();
  }

  public connect(options: ChatSocketConnectOptions = {}): boolean {
    if (typeof WebSocket === 'undefined') {
      this.setState('error');
      this.emitConnectionError('WebSocket no esta disponible en este navegador.');
      return false;
    }

    if (this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) {
      return true;
    }

    const accessToken = this.storage.token?.access_token;
    if (!accessToken) {
      this.setState('error');
      this.emitConnectionError('Token no disponible para conectar el chat.');
      return false;
    }

    this.clearReconnectTimer();
    this.intentionalClose = false;
    this.reconnectEnabled = options.reconnect ?? true;
    this.setState('connecting');

    const socket = new WebSocket(this.apiUrl.chatWebSocketUrl(accessToken));
    this.socket = socket;

    socket.onopen = () => {
      this.zone.run(() => {
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        this.setState('open');
      });
    };

    socket.onmessage = (message) => {
      this.zone.run(() => this.handleMessage(message.data));
    };

    socket.onerror = () => {
      this.zone.run(() => this.setState('error'));
    };

    socket.onclose = (event) => {
      this.zone.run(() => this.handleClose(socket, event));
    };

    return true;
  }

  public disconnect(): void {
    this.intentionalClose = true;
    this.reconnectEnabled = false;
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    this.clearHeartbeatTimer();

    if (!this.socket) {
      this.setState('closed');
      return;
    }

    const socket = this.socket;
    this.socket = null;

    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      this.setState('closing');
      socket.close(1000, 'Cierre solicitado');
      return;
    }

    this.setState('closed');
  }

  public sendPing(payload: Partial<ChatPingPayload> = {}): boolean {
    return this.sendSocketEvent('ping', {
      ...payload,
      client_ts: payload.client_ts ?? Date.now()
    });
  }

  public eventsOfType<TPayload = unknown>(type: ChatSocketEventType): Observable<ChatSocketEvent<TPayload>> {
    return this.events$.pipe(
      filter((event): event is ChatSocketEvent<TPayload> => event.type === type)
    );
  }

  private sendSocketEvent(type: ChatSocketEventType, payload: unknown): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }

    this.socket.send(JSON.stringify({ type, payload }));
    return true;
  }

  private handleMessage(data: string): void {
    const event = this.parseSocketEvent(data);

    if (!event) {
      this.emitConnectionError('Evento WebSocket de chat no valido.');
      return;
    }

    this.eventsSubject.next(event);
  }

  private handleClose(socket: WebSocket, event: CloseEvent): void {
    this.clearHeartbeatTimer();

    if (this.socket === socket) {
      this.socket = null;
    }

    if (this.intentionalClose || event.code === 1008 || !this.reconnectEnabled) {
      this.setState('closed');
      return;
    }

    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts += 1;
    this.setState('reconnecting');

    const delay = Math.min(
      this.reconnectBaseDelayMs * (2 ** Math.min(this.reconnectAttempts - 1, 4)),
      this.reconnectMaxDelayMs
    );

    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect({ reconnect: this.reconnectEnabled });
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer === null) {
      return;
    }

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private startHeartbeat(): void {
    this.clearHeartbeatTimer();
    this.heartbeatTimer = setInterval(() => this.sendPing(), this.heartbeatIntervalMs);
  }

  private clearHeartbeatTimer(): void {
    if (this.heartbeatTimer === null) {
      return;
    }

    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private parseSocketEvent(data: string): ChatSocketEvent | null {
    try {
      const parsed: unknown = JSON.parse(data);
      if (!this.isChatSocketEvent(parsed)) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private isChatSocketEvent(value: unknown): value is ChatSocketEvent {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const candidate = value as Partial<ChatSocketEvent>;
    return typeof candidate.type === 'string'
      && 'payload' in candidate
      && typeof candidate.occurred_at === 'string';
  }

  private emitConnectionError(detail: string): void {
    const payload: ChatConnectionErrorPayload = { detail };
    this.eventsSubject.next({
      type: 'connection.error',
      payload,
      occurred_at: new Date().toISOString()
    });
  }

  private setState(state: ChatSocketConnectionState): void {
    if (this.connectionStateSubject.value === state) {
      return;
    }

    this.connectionStateSubject.next(state);
  }
}
